import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Home, ArrowLeft, Search } from 'lucide-react';
import SEO from '../Components/SEO';

export default function NotFound() {
  return (
    <>
      <SEO
        title="404 - Page Not Found | DPDP Guide"
        description="The page you're looking for doesn't exist. Navigate back to explore the DPDP Act and Rules."
      />
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{
          background: 'linear-gradient(to bottom, #eff6ff, #ecfeff)',
          fontFamily: 'Inter, Arial, sans-serif',
        }}
      >
        <div className="max-w-lg w-full mx-auto px-6 py-16">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #1d4ed8, #0891b2)' }} />

            <div className="px-8 sm:px-14 py-14 flex flex-col items-center text-center">
              {/* Icon */}
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)' }}
              >
                <ShieldAlert className="w-10 h-10 text-red-500" />
              </div>

              {/* Error code */}
              <h1
                className="text-7xl font-extrabold mb-2"
                style={{
                  background: 'linear-gradient(to right, #1d4ed8, #0891b2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                404
              </h1>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                Page Not Found
              </h2>

              <p className="text-gray-500 text-sm mb-8 max-w-sm leading-relaxed">
                The page you're looking for doesn't exist or may have been moved.
                Let's get you back on track.
              </p>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
                <Link
                  to="/dpdp"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-sm shadow-sm hover:shadow-md transition w-full sm:w-auto"
                  style={{ background: 'linear-gradient(to right, #1d4ed8, #0891b2)' }}
                >
                  <Home className="w-4 h-4" />
                  Go to Homepage
                </Link>
                <button
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
              </div>

              {/* Divider */}
              <div className="w-16 h-px bg-gray-200 my-8" />

              {/* Quick links */}
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
                Quick Links
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {[
                  { label: 'DPDP Act', to: '/dpdp' },
                  { label: 'DPDP Rules', to: '/dpdp-rules' },
                  { label: 'Blog', to: '/blog' },
                  { label: 'Contact Us', to: '/contact-us' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-blue-100 bg-blue-50 text-blue-800 text-xs font-medium hover:bg-blue-100 transition"
                  >
                    <Search className="w-3 h-3" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 · DPDP Guide
          </p>
        </div>
      </div>
    </>
  );
}
